import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useGlobalMetrics } from '@/hooks/useGlobalMetrics';
import { useAnimatedCounter } from '@/hooks/useAnimatedCounter';
import { Trophy, Target, Zap } from 'lucide-react';

export function HeroVideoSection() {
  const navigate = useNavigate();
  const { data: metrics, isLoading } = useGlobalMetrics();

  const sessionsCounter = useAnimatedCounter({ 
    end: metrics?.totalSessions || 0, 
    duration: 2000,
    delay: 800 
  });

  const touchesCounter = useAnimatedCounter({ 
    end: metrics?.totalTouches || 0, 
    duration: 2000,
    delay: 900 
  });

  const skillsCounter = useAnimatedCounter({ 
    end: 62, 
    duration: 2000,
    delay: 1000 
  });

  const scrollToSignIn = () => {
    document.getElementById('sign-in')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <section className="relative min-h-[500px] h-[calc(100vh-4rem)] max-h-[900px] w-full overflow-hidden sm:min-h-[600px] md:max-h-[1000px]">
      {/* Video Container */}
      <div className="absolute inset-0 overflow-hidden">
        <iframe 
          src="https://player.vimeo.com/video/1137868082?badge=0&autopause=0&autoplay=1&muted=1&loop=1&background=1&player_id=0&app_id=58479"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full"
          style={{ minWidth: '177.77vh', minHeight: '56.25vw' }}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
          title="FutWork — Train. Track. Level Up."
          loading="lazy"
        />
      </div>

      {/* Overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#111835]/60 via-[#111835]/40 to-[#111835]/70" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
        <img 
          src="https://lztlmniinqehhxbqftsn.supabase.co/storage/v1/object/public/badge-icons/Header%20Logo_90-01.png" 
          alt="FutWork Icon" 
          className="h-48 md:h-60 mb-4 animate-fade-in"
        />
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-in">
          Train. Track. Level Up.
        </h1>
        
        <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl animate-fade-in" style={{ animationDelay: '300ms' }}>
          The ultimate soccer skills training platform for players and teams
        </p>

        {/* Animated Metrics */}
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 mb-8 animate-fade-in" style={{ animationDelay: '600ms' }}>
          {/* Sessions Completed */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="h-5 w-5 text-[#f8d613]" />
              <span className="text-3xl md:text-4xl font-bold text-white">
                {isLoading ? '...' : sessionsCounter.value.toLocaleString()}
              </span>
            </div>
            <span className="text-sm md:text-base text-white/70">Sessions Completed</span>
          </div>

          {/* Divider */}
          <div className="hidden sm:block h-12 w-px bg-white/30" />

          {/* Total Touches */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-5 w-5 text-[#f8d613]" />
              <span className="text-3xl md:text-4xl font-bold text-white">
                {isLoading ? '...' : touchesCounter.value.toLocaleString()}
              </span>
            </div>
            <span className="text-sm md:text-base text-white/70">Total Touches</span>
          </div>

          {/* Divider */}
          <div className="hidden sm:block h-12 w-px bg-white/30" />

          {/* Skills Available */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-5 w-5 text-[#f8d613]" />
              <span className="text-3xl md:text-4xl font-bold text-white">
                {skillsCounter.value}
              </span>
            </div>
            <span className="text-sm md:text-base text-white/70">Skills Available</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '900ms' }}>
          <Button
            size="lg"
            variant="outline"
            onClick={scrollToSignIn}
            className="bg-[#ffffff]/20 text-white border-[#111835]/20 hover:bg-[#ffffff]/30 backdrop-blur-sm px-8 py-6 text-lg"
          >
            Sign In
          </Button>
          <Button
            size="lg"
            onClick={() => navigate('/find-club')}
            className="bg-[#f8d613] text-[#111835] hover:bg-[#e6c800] px-8 py-6 text-lg"
          >
            Sign Up
          </Button>
        </div>
      </div>
    </section>
  );
}
